import { LinearProgress, Stack } from "@mui/material";
import { Dialog } from "../../components/dialogs/Dialog";
import { RadioGroup } from "../../components/RadioGroup";
import {
  Allowed_Mime_Types,
  EmployeeTrainingStatus,
  Error_File_Size,
  Error_Mime_Type,
  Max_File_Size,
  Training_Dialog_Mode
} from "../../constants";

import { zodResolver } from "@hookform/resolvers/zod";
import { ConfirmationDialog } from "klayowebshared";
import { isEmpty } from "lodash";
import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import axiosClient from "../../common/AxiosClient";
import { Utils } from "../../common/Utils";
import { DatePicker } from "../../components/DatePicker";
import { RequestActivityFeed } from "../../components/RequestActivityFeed";
import { CourseSelector } from "../../components/selectors/CourseSelector";
import { DocumentList } from "../../components/table/DocumentList";
import { TextField } from "../../components/TextField";
import { TrainingRequestActivity } from "../../data/attributes/TrainingRequestActivity";
import { useAppMutation } from "../../hooks/useAppMutation";
import { useAppQuery } from "../../hooks/useAppQuery";
import { useCombineLoading } from "../../hooks/useCombineLoading";
import { IPracticalTraining } from "../../interfaces/PracticalTraining";
import { IEmployeeTraining, TAddEmployeeTrainingPayload } from "../../interfaces/Training";
import { useSnackbar } from "../../providers/SnackbarProvider";
import { AddEmployeeTrainingSchema } from "../../utilities/validationSchema";

type Props = {
  theme: any;
  isOpen: boolean;
  onClose: () => void;
  isInstructorViewing: boolean;
  employee: any;
  user: any;
  onLoadTrainings: () => void;
  mode: { title: string; key: string };
  training?: IPracticalTraining | null;
  trainingOptions: any;
  isFetching: boolean;
  groupId?: string;
};

const defaultValues = {
  integrationCourse: undefined,
  state: EmployeeTrainingStatus.Approved.toString(),
  completionDate: undefined,
  commentText: "",
  formFiles: []
};

const AddEditEmployeeTrainingDialog = ({
  theme,
  isOpen,
  onClose,
  isInstructorViewing,
  employee,
  onLoadTrainings,
  mode,
  training,
  user,
  trainingOptions,
  isFetching,
  groupId
}: Props) => {
  const { showSnackbar } = useSnackbar();
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [deletedDocumentIds, setDeletedDocumentIds] = useState<string[]>([]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { dirtyFields, isValid }
  } = useForm<TAddEmployeeTrainingPayload>({
    resolver: zodResolver(AddEmployeeTrainingSchema),
    defaultValues
  });

  const isChangedForm =
    !isEmpty(dirtyFields) || Object.values(dirtyFields).some((isDirty) => isDirty);

  const { isCreate, isDetail } = useMemo(() => {
    return {
      isCreate: mode.key === Training_Dialog_Mode.add.key,
      isDetail: mode.key === Training_Dialog_Mode.details.key
    };
  }, [mode.key]);

  const { data: activity, isLoading: isGetActivityLoading } = useAppQuery<any>(
    `/EmployeeTraining/GetActivityHistory/${training?.id}`,
    {
      enabled: isOpen && !isCreate,
      select: (res) => new TrainingRequestActivity(res)
    }
  );

  const { mutateAsync: onAddByInstructor, isLoading: isAddByInstructorLoading } = useAppMutation(
    "post",
    "/EmployeeTraining/byinstructor",
    {
      config: {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      }
    }
  );
  const { mutateAsync: onEditByInstructor, isLoading: isEditByInstructorLoading } = useAppMutation(
    "put",
    "/EmployeeTraining/byinstructor",
    {
      config: {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      }
    }
  );
  const { mutateAsync: onAddByEmployee, isLoading: isAddByEmployeeLoading } = useAppMutation(
    "post",
    "/EmployeeTraining",
    {
      config: {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      }
    }
  );
  const { mutateAsync: onFeedbackByEmployee, isLoading: isFeedbackByEmployeeLoading } =
    useAppMutation("post", "/EmployeeTrainingValidationRequest/RespondChange", {
      config: {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      }
    });

  const isLoading = useCombineLoading([
    isAddByEmployeeLoading,
    isAddByInstructorLoading,
    isFeedbackByEmployeeLoading,
    isEditByInstructorLoading
  ]);

  const documents = useMemo(() => {
    const documents = activity?.documents?.list || [];

    return documents.map((doc: any) => {
      if (deletedDocumentIds.includes(doc.documentId)) {
        return {
          ...doc,
          pendingDelete: true
        };
      }

      return doc;
    });
  }, [activity?.documents?.list, deletedDocumentIds]);

  const onShowWarnCloseDialog = () => {
    if (isChangedForm) return setShowConfirmClose(true);

    onCloseDialog();
  };
  const onCloseDialog = () => {
    onClose();
    setDeletedDocumentIds([]);
  };

  const onSubmit = async (values: TAddEmployeeTrainingPayload) => {
    if (isInstructorViewing) {
      isCreate ? await onAddTrainingByInstructor(values) : await onEditTrainingByInstructor(values);
    } else {
      await onAddTrainingByEmployee(values);
    }
    onCloseDialog();

    await onLoadTrainings();
  };

  const onAddTrainingByInstructor = async (values: TAddEmployeeTrainingPayload) => {
    const formData = new FormData();
    formData.append("EmployeeId", employee?.employeeId);
    formData.append("State", (values.state || 0)?.toString());

    if (values.integrationCourse?.id) {
      formData.append("IntegrationCourseId", values.integrationCourse.id);
    }

    if (values.completionDate) {
      formData.append("CompletionDate", values.completionDate);
    }

    if (values.commentText) {
      formData.append("CommentText", values.commentText);
    }

    if (values.formFiles) {
      Object.entries(values.formFiles).map((f: any) => {
        if (f[1] !== null) formData.append("FormFiles", f[1] as any);
      });
    }

    if (groupId) {
      formData.append("GroupId", groupId);
    }

    await onAddByInstructor(formData as any);
  };

  const onEditTrainingByInstructor = async (values: TAddEmployeeTrainingPayload) => {
    const formData = new FormData();

    if (training?.id) {
      formData.append("EmployeeTrainingId", training?.id);
    }

    if (values.state) {
      formData.append("State", values.state?.toString());
    }

    if (values.completionDate) {
      formData.append("CompletionDate", values.completionDate);
    }

    if (values.commentText) {
      formData.append("CommentText", values.commentText);
    }

    if (values.formFiles) {
      Object.entries(values.formFiles).map((f: any) => {
        if (f[1] !== null) formData.append("FormFiles", f[1] as any);
      });
    }
    if (deletedDocumentIds) {
      deletedDocumentIds.forEach((id: any) => {
        formData.append("DeletedEvidences", id);
      });
    }

    if (groupId) {
      formData.append("GroupId", groupId);
    }

    await onEditByInstructor(formData as any);
  };

  const onAddTrainingByEmployee = async (values: TAddEmployeeTrainingPayload) => {
    if (isCreate) return onAddEmployeeTraining(values);

    return onEmployeeTrainingFeedback(values);
  };

  const onAddEmployeeTraining = async (values: TAddEmployeeTrainingPayload) => {
    const formData = new FormData();

    if (values.integrationCourse?.id) {
      formData.append("Training.IntegrationCourseId", values.integrationCourse.id);
    }

    if (values.completionDate) {
      formData.append("Training.CompletionDate", values.completionDate);
    }

    if (values.commentText) {
      formData.append("Training.CommentText", values.commentText);
    }

    if (values.formFiles) {
      Object.entries(values.formFiles).map((f: any) => {
        if (f[1] !== null) formData.append("Training.FormFiles", f[1] as any);
      });
    }
    await onAddByEmployee(formData as any);
  };

  const onEmployeeTrainingFeedback = async (values: TAddEmployeeTrainingPayload) => {
    const formData = new FormData();

    if (training?.id) {
      formData.append(
        "EmployeeTrainingValidationRequestId",
        training?.employeeTrainingValidationRequestId
      );
    }

    if (values.completionDate) {
      formData.append("CompletionDate", values.completionDate);
    }

    if (values.commentText) {
      formData.append("CommentText", values.commentText);
    }

    if (values.formFiles) {
      Object.entries(values.formFiles).map((f: any) => {
        if (f[1] !== null) formData.append("FormFiles", f[1] as any);
      });
    }

    await onFeedbackByEmployee(formData as any);
  };

  const onDocumentClick = async (e: any, doc: any) => {
    try {
      const response = await axiosClient.get(`/EmployeeTraining/download/${doc.documentId}`, {
        responseType: "blob"
      });

      const blob = new Blob([response.data], { type: response.headers["content-type"] });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", doc.documentName);
      document.body.appendChild(link);
      link.click();

      showSnackbar(`Downloaded document ${doc.documentName}`, "info");
    } catch (error) {}
  };

  const onCancelClose = () => {
    setShowConfirmClose(false);
  };
  const onAcceptClose = () => {
    setShowConfirmClose(false);
    onCloseDialog();
  };

  const onDeleteDocument = (doc: any) => {
    setDeletedDocumentIds((prev) => [...prev, doc.documentId]);
  };

  useEffect(() => {
    if (!training || !isOpen) return reset(defaultValues);

    reset({
      ...defaultValues,
      integrationCourse: {
        id: training.integrationCourseId,
        name: training.name
      },
      completionDate: training.completedDate,
      state: String(training.statusValue || EmployeeTrainingStatus.Approved)
    });
  }, [training, reset, isOpen]);

  const getActions = () => {
    if (isDetail)
      return [
        {
          label: "Close",
          variant: "filled",
          onClick: onCloseDialog,
          type: "button"
        }
      ];

    return [
      {
        label: isInstructorViewing ? "Save" : "Submit request",
        variant: "filled",
        type: "submit",
        form: "add-training-form",
        isLoading: isLoading,
        disabled: !isValid
      },
      {
        label: "Cancel",
        variant: "outlined",
        onClick: onShowWarnCloseDialog,
        type: "button",
        form: "add-training-form"
      }
    ];
  };

  return (
    <>
      <Dialog
        open={isOpen}
        theme={theme}
        onClose={onShowWarnCloseDialog}
        fullWidth={true}
        title={mode.title}
        maxWidth='sm'
        actions={isOpen ? getActions() : []}
      >
        {isGetActivityLoading ? (
          <LinearProgress />
        ) : (
          <form id='add-training-form' onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={5}>
              <Controller
                name='integrationCourse'
                control={control}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <CourseSelector
                      label='Training (required)'
                      options={trainingOptions}
                      onChange={(e: ChangeEvent<HTMLInputElement>, course: IEmployeeTraining) => {
                        field.onChange(course);
                      }}
                      placeholder='Select training'
                      value={field.value}
                      errorMessage={error?.message}
                      error={!!error?.message}
                      isLoading={isFetching}
                      disabled={!isCreate}
                    />
                  );
                }}
              />

              {!isDetail ? (
                <>
                  {isInstructorViewing && (
                    <Controller
                      name='state'
                      control={control}
                      render={({ field }) => (
                        <RadioGroup
                          label='Training status'
                          items={[
                            {
                              value: EmployeeTrainingStatus.Approved,
                              label: "Approved"
                            },
                            {
                              value: EmployeeTrainingStatus.PendingApproval,
                              label: "Pending manager feedback"
                            },
                            {
                              value: EmployeeTrainingStatus.NeedsEmployeeFeedback,
                              label: "Needs employee feedback"
                            }
                          ]}
                          value={field.value}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            field.onChange(e.target.value);
                          }}
                        />
                      )}
                    />
                  )}

                  <Controller
                    name='formFiles'
                    control={control}
                    render={({ field }) => (
                      <DocumentList
                        theme={theme}
                        label='Evidence'
                        files={field.value}
                        onFilesChange={(e: ChangeEvent<HTMLInputElement>, files: any[]) => {
                          field.onChange(files);
                        }}
                        allowedMimeTypes={Allowed_Mime_Types}
                        maxFileSize={Max_File_Size}
                        errorMimeType={Error_Mime_Type}
                        errorFileSize={Error_File_Size}
                        {...(isInstructorViewing && {
                          documents: documents
                        })}
                        onViewDocument={onDocumentClick}
                        onDeleteDocument={(e: any, doc: any) => {
                          onDeleteDocument(doc);
                        }}
                      />
                    )}
                  />

                  <Controller
                    name='completionDate'
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <DatePicker
                        theme={theme}
                        label={"Completion date (required)"}
                        onChange={(date: Date) => {
                          field.onChange(Utils.toApiDate(date));
                        }}
                        value={field.value ? new Date(field.value) : undefined}
                        allowPast={true}
                        allowEmpty={true}
                        textFieldProps={{ fullWidth: true, readOnly: true }}
                        error={!!error?.message}
                        errorMessage={error?.message}
                      />
                    )}
                  />

                  <Controller
                    name='commentText'
                    control={control}
                    render={({ field }) => (
                      <TextField
                        value={field.value}
                        label='Comments'
                        placeholder='Add any comments or notes'
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          field.onChange(e.target.value)
                        }
                      />
                    )}
                  />
                </>
              ) : (
                <Controller
                  name='completionDate'
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <DatePicker
                      theme={theme}
                      label={"Completion date (require)"}
                      onChange={(date: Date) => {
                        field.onChange(Utils.toApiDate(date));
                      }}
                      value={field.value ? new Date(field.value) : undefined}
                      allowPast={true}
                      allowEmpty={true}
                      textFieldProps={{ fullWidth: true, readOnly: true }}
                      error={!!error?.message}
                      errorMessage={error?.message}
                      minDate={new Date()}
                      disabled
                    />
                  )}
                />
              )}

              {!isCreate && activity && (
                <RequestActivityFeed
                  user={user}
                  employee={employee}
                  activity={activity}
                  onDocumentClick={onDocumentClick}
                />
              )}
            </Stack>
          </form>
        )}
        {showConfirmClose && (
          <ConfirmationDialog
            title='Cancel?'
            theme={theme}
            question={`Are you sure you want to cancel this ${isCreate ? "add" : "edit"} training request?`}
            cancelButton='No'
            acceptButton='Yes'
            onCancel={onCancelClose}
            onAccept={onAcceptClose}
          />
        )}
      </Dialog>
    </>
  );
};

export default AddEditEmployeeTrainingDialog;
